import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

/**
 * Manages the popovers of a quick access label. At most one popover is open at a time ("style" is always
 * available, labels can add their own keys, e.g. "offset").
 *
 * - Opening or toggling a popover first asks the open one to close. The title style control registers a close
 *   handler (via `registerCloseHandler`) that may veto this, e.g. while its color picker is open.
 * - A mousedown outside of both the label (`componentRef`) and the rendered popover (`popoverRef`) closes the
 *   active popover. Popovers that handle this themselves (e.g. a dropdown with its own anchor) simply don't
 *   attach `popoverRef`, then the outside click is ignored here.
 */
export function useLabelPopovers<Extra extends string = never>(componentRef: RefObject<HTMLElement | null>) {
  type Key = "style" | Extra;

  const [active, setActive] = useState<Key | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeHandlerRef = useRef<(() => boolean) | null>(null);

  const registerCloseHandler = useCallback((handler: (() => boolean) | null) => {
    closeHandlerRef.current = handler;
  }, []);

  /** Closes the active popover. Returns `false` if it vetoed, so callers must not continue their action. */
  const requestClose = useCallback(() => {
    if (closeHandlerRef.current?.() === false) {
      return false;
    }
    setActive(null);
    return true;
  }, []);

  /** Opens `key`, or closes it if it is already open. Does nothing if the active popover vetoes closing. */
  const toggle = useCallback(
    (key: Key) => {
      if (!requestClose()) {
        return;
      }
      if (active !== key) {
        setActive(key);
      }
    },
    [active, requestClose],
  );

  const isOpen = useCallback((key: Key) => active === key, [active]);

  useEffect(() => {
    if (active === null) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const popover = popoverRef.current;
      const component = componentRef.current;
      const target = event.target as Node;
      if (popover && component && !popover.contains(target) && !component.contains(target)) {
        requestClose();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [active, componentRef, requestClose]);

  return { active, isOpen, toggle, requestClose, popoverRef, registerCloseHandler };
}
